import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { toBooleanFromBit } from './utils/productHelpers';
import useEcPromoBannerSettings from './hooks/useEcPromoBannerSettings';

function ProductPromoBannerTab({
  formData,
  sendDataToParent,
  readOnly = false,
}) {
  const { maxCount } = useEcPromoBannerSettings();
  const [activePromoCount, setActivePromoCount] = useState(0);

  useEffect(() => {
    const fetchActiveCount = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/ec-product/getAll`);
        const products = response.data?.data || [];
        const count = products.filter(
          (product) =>
            toBooleanFromBit(product.IsPromoBanner, false)
            && product.ProductId !== formData?.ProductId,
        ).length;
        setActivePromoCount(count);
      } catch (error) {
        console.error(error);
      }
    };

    fetchActiveCount();
  }, [formData?.ProductId]);

  const hasPromoImage = useMemo(
    () => (formData?.Images || []).some(
      (image) => String(image.ImageType || '').toLowerCase() === 'promo',
    ),
    [formData?.Images],
  );

  const isPromoEnabled = !!formData?.IsPromoBanner;
  const atMaxCount = isPromoEnabled === false && activePromoCount >= maxCount;

  const updateField = (patch) => {
    if (readOnly || !sendDataToParent) return;
    sendDataToParent({ ...formData, ...patch });
  };

  const handleToggle = (checked) => {
    if (checked && atMaxCount) {
      return;
    }
    updateField({ IsPromoBanner: checked });
  };

  return (
    <div className="card">
      <div className="card-body">
        <p className="text-muted small mb-3">
          Maximum {maxCount} products can have promo banners active on the homepage.
        </p>

        {atMaxCount ? (
          <div className="alert alert-warning py-2">
            The maximum number of active promo banners ({maxCount}) is already in use.
          </div>
        ) : null}

        <div className="row g-3">
          <div className="col-md-4">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="product-promo-active"
                checked={isPromoEnabled}
                disabled={readOnly || atMaxCount}
                onChange={(e) => handleToggle(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="product-promo-active">
                Active promo banner
              </label>
            </div>
          </div>

          <div className="col-md-4">
            <label className="form-label" htmlFor="product-promo-display-order">Display order</label>
            <input
              id="product-promo-display-order"
              type="number"
              className="form-control"
              min="0"
              value={formData?.PromoBannerDisplayOrder ?? 0}
              disabled={readOnly}
              onChange={(e) => updateField({ PromoBannerDisplayOrder: Number(e.target.value) || 0 })}
            />
          </div>

          <div className="col-12">
            <label className="form-label" htmlFor="product-promo-title">Promo title (optional)</label>
            <textarea
              id="product-promo-title"
              className="form-control"
              rows={3}
              value={formData?.PromoBannerTitle || ''}
              disabled={readOnly}
              placeholder="Leave blank to use product name. Use new lines for multi-line overlay text."
              onChange={(e) => updateField({ PromoBannerTitle: e.target.value })}
            />
          </div>

          <div className="col-12">
            <p className="text-muted small mb-0">
              Upload the promo image from the <strong>Upload Images</strong> tab using image type
              {' '}
              <strong>Promo Banner</strong>
              {isPromoEnabled ? '' : ' after enabling the promo banner option above'}.
              {!hasPromoImage && isPromoEnabled ? (
                <span className="text-warning d-block mt-1">
                  A promo banner image is required before you can save with promo enabled.
                </span>
              ) : null}
              {hasPromoImage ? (
                <span className="text-success d-block mt-1">Promo banner image uploaded.</span>
              ) : null}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductPromoBannerTab;
